/**
 * Detecção da extensão myGROW ADT Connect (S-070, item 2).
 *
 * A extensão publica um `probe.json` acessível apenas às origens do myGROW
 * (`web_accessible_resources` no manifesto dela). Se a página consegue lê-lo, ela está
 * instalada. Não há API do Chrome para "listar extensões", e nem queremos uma: este
 * caminho não dá ao myGROW visibilidade sobre nada além da própria extensão.
 *
 * 🔶 **O resultado avisa, nunca bloqueia.** A detecção pode dar falso negativo em
 * navegador que não é Chromium, com um ID que ainda não conhecemos, ou sob política que
 * bloqueie o recurso. Travar o botão por causa disso seria pior que deixar a pessoa
 * tentar e ver o que acontece.
 */

/** Nome exibido da extensão, conferido no probe para não aceitar o arquivo de outra. */
export const ADT_EXTENSION_NAME = "myGROW ADT Connect";

/** IDs conhecidos, em ordem de tentativa.
 *
 * ⚠️ **A ordem importa e não é alfabética nem histórica.** `ADT_EXTENSION_STORE_URL` é
 * montada a partir de `[0]`, então o índice zero tem de ser o ID **publicado**. Reordenar
 * este array aponta o botão "instalar" para um 404 permanente, e reprova o teste do
 * href em `adt-session-panel.test.tsx`, que casa o ID publicado literalmente. */
export const ADT_EXTENSION_IDS = [
  // Chrome Web Store. Atribuído no primeiro upload (25/08) e estável a partir dali.
  "kcekjnaofphdpfbbkhnjdffpkkfibecj",
  // Carregada sem compactação a partir do repositório, ID fixado pela chave pública no
  // manifesto. Fica na lista porque as duas versões convivem enquanto o time migra, e
  // porque é a que roda em desenvolvimento.
  "fapfiajeinfifhalabjnnamhaafbnabc",
];

/** Página da extensão na Chrome Web Store, para quem ainda não a tem.
 *
 * Derivada do primeiro ID da lista, que é o publicado. Sem o slug de propósito: a store
 * responde 301 de `/detail/<id>` para `/detail/<slug>/<id>`, então o link sobrevive a
 * uma renomeação do item, que o slug não sobreviveria.
 */
export const ADT_EXTENSION_STORE_URL = `https://chromewebstore.google.com/detail/${ADT_EXTENSION_IDS[0]}`;

export interface AdtExtensionInfo {
  version: string;
}

async function probeId(id: string): Promise<AdtExtensionInfo | null> {
  try {
    const r = await fetch(`chrome-extension://${id}/probe.json`);
    if (!r.ok) return null;
    const j = await r.json();
    // Confere que é o NOSSO probe pelo nome, e não só pela forma: outra extensão
    // poderia servir um arquivo com esse caminho, e aceitar qualquer resposta diria
    // "instalada" para a errada.
    const isOurs =
      j?.name === ADT_EXTENSION_NAME && typeof j?.version === "string";
    return isOurs ? { version: j.version } : null;
  } catch {
    // Extensão ausente rejeita o fetch, não devolve 404.
    return null;
  }
}

export async function detectAdtExtension(
  ids: readonly string[] = ADT_EXTENSION_IDS,
): Promise<AdtExtensionInfo | null> {
  for (const id of ids) {
    const found = await probeId(id);
    if (found) return found;
  }
  return null;
}
